import { Pencil, Trash2 } from "lucide-react";
import { type CSSProperties, forwardRef, type HTMLAttributes } from "react";
import { DAY_KEYS, type DayKey } from "@/lib/days";
import {
  type DayWidgetWeekEntry,
  emptyDayData,
  emptyWeekData,
  type WeekWidgetWeekEntry,
  type WidgetConfig,
  type WidgetWeekEntry,
} from "@/lib/schema";
import { getWidgetModule } from "@/widgets/registry";

interface Props extends Omit<HTMLAttributes<HTMLElement>, "onChange"> {
  widget: WidgetConfig;
  selectedDay: DayKey;
  allWidgets: WidgetConfig[];
  editing?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  style?: CSSProperties;
}

function synthesizeAllEntries(widgets: WidgetConfig[]): WidgetWeekEntry[] {
  return widgets.map((w): WidgetWeekEntry => {
    const module = getWidgetModule(w.kind);
    if (module?.scope === "week") {
      const entry: WeekWidgetWeekEntry = {
        config: w as WeekWidgetWeekEntry["config"],
        week: emptyWeekData(w.kind),
      };
      return entry;
    }
    const entry: DayWidgetWeekEntry = {
      config: w as DayWidgetWeekEntry["config"],
      week: emptyWeekData(w.kind),
      days: Object.fromEntries(
        DAY_KEYS.map((k) => [k, emptyDayData(w.kind)]),
      ) as DayWidgetWeekEntry["days"],
    };
    return entry;
  });
}

export const WidgetPreviewCard = forwardRef<HTMLElement, Props>(
  function WidgetPreviewCard(
    {
      widget,
      selectedDay,
      allWidgets,
      editing = false,
      onEdit,
      onDelete,
      className,
      ...rest
    },
    ref,
  ) {
    const module = getWidgetModule(widget.kind);
    const classes = ["widget-preview-card", editing ? "editing" : "", className]
      .filter(Boolean)
      .join(" ");
    return (
      <section
        ref={ref}
        className={classes}
        data-testid={`widget-preview-${widget.id}`}
        {...rest}
      >
        {!editing ? (
          <header className="widget-preview-header">
            <div className="widget-preview-actions">
              <button
                type="button"
                className="btn ghost icon-btn"
                onClick={onEdit}
                data-testid={`widget-edit-${widget.id}`}
              >
                <Pencil size={16} />
                Rediger
              </button>
              <button
                type="button"
                className="btn danger icon-btn"
                onClick={onDelete}
                data-testid={`widget-delete-${widget.id}`}
              >
                <Trash2 size={16} />
                Slett
              </button>
            </div>
          </header>
        ) : null}
        <div className="widget-preview-body">
          {!module ? (
            <p className="empty">Ukjent widget-type «{widget.kind}».</p>
          ) : module.scope === "day" ? (
            <module.DayView
              config={widget}
              data={emptyDayData(widget.kind)}
              weekData={emptyWeekData(widget.kind)}
              dayKey={selectedDay}
              onChangeDay={() => {}}
              onChangeWeek={() => {}}
              readOnly
            />
          ) : (
            <module.WeekView
              config={widget}
              weekData={emptyWeekData(widget.kind)}
              allWidgets={synthesizeAllEntries(allWidgets)}
              previousWeek={null}
              readOnly
            />
          )}
        </div>
      </section>
    );
  },
);
